export type TablePaginationAction = 'page' | 'maxOnPage' | 'sort'

export type TableSortDirection = 'asc' | 'desc'

export type TableSortMeta = {
  column: string
  direction: TableSortDirection
}

/** UniClientData.RA_SCROLLER / RA_MAXONPAGE. Sort uses scroller: Swing sortTable не ставит reloadActions, но webui держит page-slice. */
export const TABLE_PAGINATION_RELOAD_ACTION: Record<TablePaginationAction, number> = {
  page: 1,
  maxOnPage: 2,
  sort: 1,
}

export function normalizeTableSortDirection(value: unknown): TableSortDirection {
  return String(value ?? '').trim().toLowerCase() === 'desc' ? 'desc' : 'asc'
}

/** Swing IKey.ASC / IKey.DESC — onLoad часто делает `.toUpperCase()`. */
export function toWireTableSortDirection(value: unknown): 'ASC' | 'DESC' {
  return normalizeTableSortDirection(value) === 'desc' ? 'DESC' : 'ASC'
}

export function readTableSortMeta(
  fields: Record<string, unknown> | null | undefined,
  params: Record<string, unknown> | null | undefined,
  tableName: string,
): TableSortMeta {
  const keys = tablePaginationFieldKeys(tableName)
  const fromFields = fields && typeof fields === 'object' ? fields : {}
  const fromParams = params && typeof params === 'object' ? params : {}
  const column = String(
    readReloadFieldScalar(fromFields[keys.sortColumn]) ??
      readReloadFieldScalar(fromParams[keys.sortColumn]) ??
      '',
  ).trim()
  const direction = normalizeTableSortDirection(
    readReloadFieldScalar(fromFields[keys.sortDirection]) ??
      readReloadFieldScalar(fromParams[keys.sortDirection]),
  )
  return { column, direction }
}

export function parseTablePaginationAction(value: unknown): TablePaginationAction | null {
  const raw = String(value ?? '').trim()
  if (raw === 'maxOnPage') return 'maxOnPage'
  if (raw === 'sort') return 'sort'
  if (raw === 'page') return 'page'
  return null
}

export function tablePaginationFieldKeys(tableName: string) {
  const name = String(tableName ?? '').trim()
  return {
    page: `${name}__page`,
    maxOnPage: `${name}__maxOnPage`,
    action: `${name}__action`,
    size: `${name}__size`,
    sortColumn: `${name}__sortColumn`,
    sortDirection: `${name}__sortDirection`,
  }
}

export function readReloadFieldScalar(field: unknown): string | number | undefined {
  if (field == null) return undefined
  if (typeof field === 'number' || typeof field === 'string') return field
  if (typeof field === 'object' && field !== null && 'value' in field) {
    const value = (field as { value?: unknown }).value
    if (value == null) return undefined
    if (typeof value === 'number' || typeof value === 'string') return value
    return String(value)
  }
  return undefined
}

export function extractTablePaginationClientData(
  fields: Record<string, unknown>,
  tableName: string,
): { reloadActions: Record<string, number>; params: Record<string, string | number> } | null {
  const name = String(tableName ?? '').trim()
  if (!name) return null

  const keys = tablePaginationFieldKeys(name)
  const page = readReloadFieldScalar(fields[keys.page])
  const maxOnPage = readReloadFieldScalar(fields[keys.maxOnPage])
  const actionRaw = readReloadFieldScalar(fields[keys.action])

  if (page === undefined && maxOnPage === undefined) return null

  const action = parseTablePaginationAction(actionRaw)
  if (!action) return null

  const params: Record<string, string | number> = {}
  if (page !== undefined) params[keys.page] = page
  if (maxOnPage !== undefined) params[keys.maxOnPage] = maxOnPage

  const sortColumn = readReloadFieldScalar(fields[keys.sortColumn])
  const sortDirection = readReloadFieldScalar(fields[keys.sortDirection])
  if (sortColumn) {
    params[keys.sortColumn] = sortColumn
    if (sortDirection) params[keys.sortDirection] = sortDirection
  }

  return {
    reloadActions: { [name]: TABLE_PAGINATION_RELOAD_ACTION[action] },
    params,
  }
}

export function buildTablePaginationReloadFields(
  tableName: string,
  options: {
    page: number
    maxOnPage: number
    action: TablePaginationAction
    sortColumn?: string
    sortDirection?: string
  },
): Record<string, string | number> {
  const keys = tablePaginationFieldKeys(tableName)
  const fields: Record<string, string | number> = {
    [keys.page]: options.page,
    [keys.maxOnPage]: options.maxOnPage,
    [keys.action]: options.action,
  }
  if (options.sortColumn) {
    fields[keys.sortColumn] = options.sortColumn
    if (options.sortDirection) {
      fields[keys.sortDirection] = toWireTableSortDirection(options.sortDirection)
    }
  }
  return fields
}

export type TablePaginationEmptyPageClamp = {
  page: number
  pages: number
  totalItems: number
  maxOnPage: number
}

/** Страница пагинации вернула 0 строк при переходе (не смена maxOnPage). */
export function isTablePaginationEmptyPageResponse(options: {
  page: number
  currentItemsCount: number
  paginationAction?: TablePaginationAction
}): boolean {
  if (options.paginationAction && options.paginationAction !== 'page') return false
  const page = Math.max(1, Number(options.page) || 1)
  const currentItemsCount = Math.max(0, Number(options.currentItemsCount) || 0)
  return page > 1 && currentItemsCount === 0
}

/** Пересчёт meta после пустой страницы: остаёмся на предыдущей, total = (page-1)*maxOnPage. */
export function resolveTablePaginationEmptyPageCorrection(options: {
  page: number
  maxOnPage: number
}): TablePaginationEmptyPageClamp {
  const page = Math.max(1, Number(options.page) || 1)
  const maxOnPage = Math.max(1, Number(options.maxOnPage) || 1)
  const correctedPage = Math.max(1, page - 1)
  const totalItems = correctedPage * maxOnPage
  return {
    page: correctedPage,
    pages: correctedPage,
    totalItems,
    maxOnPage,
  }
}

export function buildTablePaginationEmptyPageFieldPatch(
  tableName: string,
  options: {
    snapshotTableField: unknown
    clamp: TablePaginationEmptyPageClamp
  },
): Record<string, unknown> {
  const keys = tablePaginationFieldKeys(tableName)
  return {
    [tableName]: options.snapshotTableField,
    [keys.page]: String(options.clamp.page),
    [keys.maxOnPage]: String(options.clamp.maxOnPage),
    [keys.size]: String(options.clamp.totalItems),
  }
}

export function buildTablePaginationEmptyPageParamsPatch(
  tableName: string,
  clamp: TablePaginationEmptyPageClamp,
): Record<string, string> {
  const keys = tablePaginationFieldKeys(tableName)
  return {
    [keys.page]: String(clamp.page),
    [keys.maxOnPage]: String(clamp.maxOnPage),
    [keys.size]: String(clamp.totalItems),
  }
}

export function resolveTablePaginationMetaFallback(options: {
  page: number
  maxOnPage: number
  currentItemsCount: number
  totalItemsFromBackend: number | null
}): { totalItems: number; pages: number } {
  const page = Math.max(1, Number(options.page) || 1)
  const maxOnPage = Math.max(1, Number(options.maxOnPage) || 1)
  const currentItemsCount = Math.max(0, Number(options.currentItemsCount) || 0)
  const totalItemsFromBackend = options.totalItemsFromBackend

  if (totalItemsFromBackend !== null && Number.isFinite(totalItemsFromBackend)) {
    const totalItems = totalItemsFromBackend
    return {
      totalItems,
      pages: totalItems && maxOnPage ? Math.ceil(totalItems / maxOnPage) : 1,
    }
  }

  if (currentItemsCount === maxOnPage) {
    return {
      totalItems: page * maxOnPage + maxOnPage,
      pages: Math.max(page + 1, 2),
    }
  }

  if (currentItemsCount > maxOnPage) {
    return {
      totalItems: currentItemsCount,
      pages: Math.ceil(currentItemsCount / maxOnPage),
    }
  }

  if (currentItemsCount > 0) {
    if (page === 1) {
      return { totalItems: currentItemsCount, pages: 1 }
    }
    return {
      totalItems: (page - 1) * maxOnPage + currentItemsCount,
      pages: page,
    }
  }

  if (page > 1) {
    const correctedPage = page - 1
    return {
      totalItems: correctedPage * maxOnPage,
      pages: correctedPage,
    }
  }

  return { totalItems: 0, pages: 1 }
}
